import { Box, Typography, ButtonBase, Badge } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { TONES } from '@/components/dashboardTones';

/**
 * A dashboard navigation tile — soft, elevated, with a gradient icon badge and
 * a corner arrow. Matches the clinic app's Tile so both apps feel the same.
 *
 * `featured` is the larger variant used by the two tiles that lead the
 * dashboard. It changes size and weight only: same shape, same shadow
 * language, same arrow, so the pair reads as the same family of control
 * rather than a different component that happens to sit above.
 */
export default function DashboardTile({
  icon, title, desc, tone = TONES.primary, onClick, badgeCount = 0, featured = false,
}) {
  const theme = useTheme();
  const Arrow = theme.direction === 'rtl' ? ArrowBackIcon : ArrowForwardIcon;
  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
        textAlign: 'start', width: '100%', height: '100%',
        minHeight: featured ? 184 : 142,
        borderRadius: '22px', p: featured ? 2.5 : 2, bgcolor: 'background.paper',
        boxShadow: `0 10px 26px -16px ${alpha(tone, 0.55)}`,
        transition: 'transform .18s ease, box-shadow .18s ease',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 16px 32px -16px ${alpha(tone, 0.78)}` },
        '&:active': { transform: 'translateY(0)' },
        '&.Mui-focusVisible': { outline: `2px solid ${tone}`, outlineOffset: 2 },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
      }}
    >
      <Arrow aria-hidden sx={{ position: 'absolute', insetInlineStart: 15, top: 16, fontSize: 16, color: 'text.disabled' }} />
      <Badge color="error" badgeContent={badgeCount} max={99} overlap="circular">
        <Box sx={{
          width: featured ? 58 : 46, height: featured ? 58 : 46,
          borderRadius: featured ? '19px' : '15px',
          display: 'grid', placeItems: 'center', color: '#fff', mb: featured ? 1.75 : 1.25,
          background: `linear-gradient(135deg, ${tone} 0%, ${alpha(tone, 0.82)} 100%)`,
          boxShadow: `0 8px 16px -8px ${alpha(tone, 0.7)}`,
          '& svg': { fontSize: featured ? 30 : 24 },
        }}>
          {icon}
        </Box>
      </Badge>
      <Typography sx={{ fontSize: featured ? 17 : 14.5, fontWeight: 800, lineHeight: 1.3, color: 'text.primary' }}>{title}</Typography>
      <Typography sx={{
        fontSize: featured ? 12.75 : 12, color: 'text.secondary', mt: 0.25, lineHeight: 1.4,
        display: '-webkit-box', WebkitLineClamp: featured ? 3 : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
      }}>
        {desc}
      </Typography>
      <Box sx={{ flex: 1 }} />
    </ButtonBase>
  );
}
